import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import { SearchIcon, XIcon } from "lucide-react";
import React from "react";

type SearchInputProps = {
  value: string;
  onSubmit: (value: string) => void;
  onClear: () => void;
  placeholder?: string;
  className?: string;
};

function SearchInput({
  value: committedValue,
  onSubmit,
  onClear,
  placeholder = "Search...",
  className,
}: SearchInputProps) {
  const [localValue, setLocalValue] = React.useState(committedValue);
  const prevCommitted = React.useRef(committedValue);

  // Sync external changes (e.g. back/forward navigation)
  if (committedValue !== prevCommitted.current) {
    prevCommitted.current = committedValue;
    setLocalValue(committedValue);
  }

  const handleSubmit = () => {
    const trimmed = localValue.trim();
    if (trimmed !== committedValue) {
      onSubmit(trimmed);
    }
  };

  const handleClear = () => {
    setLocalValue("");
    onClear();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleClear();
    }
  };

  const dirty = localValue.trim() !== committedValue;

  return (
    <InputGroup className={cn("max-w-sm flex-1", className)}>
      <InputGroupInput
        type="text"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
      />
      <InputGroupAddon align="inline-end">
        {localValue && (
          <InputGroupButton
            variant="ghost"
            size="icon-xs"
            onClick={handleClear}
            aria-label="Clear search"
          >
            <XIcon />
          </InputGroupButton>
        )}
        <InputGroupButton
          variant={dirty ? "default" : "ghost"}
          size="icon-xs"
          onClick={handleSubmit}
          disabled={!dirty}
          aria-label="Search"
        >
          <SearchIcon />
        </InputGroupButton>
      </InputGroupAddon>
    </InputGroup>
  );
}

export { SearchInput, type SearchInputProps };
